import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useTranslation } from 'react-i18next';

import type { ItilTranslationStatus } from '../types';
import { itilColors, itilColumnSx, itilFieldBoxSx } from '../styles';
import ItilStatusBadge from './ItilStatusBadge';
import ItilTranslateActions from './ItilTranslateActions';

type Props = {
  label: string;
  required?: boolean;
  originalValue: string;
  onOriginalChange: (value: string) => void;
  translatedText: string;
  status: ItilTranslationStatus;
  errorMessage?: string | null;
  onTranslate: () => void;
  onContinueOriginal?: () => void;
  readOnly?: boolean;
  inheritedBadge?: string;
  showPermLock?: boolean;
  maxLength?: number;
};

function ItilDualColumnField({
  label,
  required = false,
  originalValue,
  onOriginalChange,
  translatedText,
  status,
  errorMessage,
  onTranslate,
  onContinueOriginal,
  readOnly = false,
  inheritedBadge,
  showPermLock = false,
  maxLength = 5000
}: Props) {
  const { t } = useTranslation('global');

  const hint = () => {
    if (status === 'stale') {
      return t('itilGenius.messages.staleHint');
    }
    if (status === 'ready') {
      return t('itilGenius.messages.readyHint');
    }
    if (status === 'storedAsOriginal') {
      return t('itilGenius.messages.storedOriginalHint');
    }
    return null;
  };

  const renderItilBody = () => {
    if (status === 'loading') {
      return (
        <Box sx={{ p: 1.5 }}>
          {[95, 88, 72, 80].map((w) => (
            <Skeleton
              key={w}
              height={12}
              sx={{ mb: 1, width: `${w}%`, borderRadius: 1 }}
              variant="rounded"
            />
          ))}
        </Box>
      );
    }

    if (status === 'error') {
      return (
        <Box
          sx={{
            m: 1.5,
            p: 1.5,
            bgcolor: '#fdeceb',
            border: '1px solid #f3c9c6',
            borderRadius: '7px'
          }}
        >
          <Typography
            sx={{ fontWeight: 700, color: '#b3261e', fontSize: '12.5px', mb: 1 }}
          >
            ! {t('itilGenius.messages.serviceUnavailableTitle')}
          </Typography>
          <Typography sx={{ fontSize: '12.5px', color: '#7c3a36', mb: 0 }}>
            {errorMessage || t('itilGenius.messages.serviceUnavailable')}
          </Typography>
        </Box>
      );
    }

    if (status === 'skipped' || status === 'storedAsOriginal') {
      return (
        <Box
          sx={{
            m: 1.5,
            p: 1.5,
            bgcolor: '#f4f6f9',
            border: '1px dashed #c9d4e2',
            borderRadius: '7px',
            fontSize: '12.5px',
            color: itilColors.inkSoft
          }}
        >
          {t('itilGenius.messages.skippedBody')}
        </Box>
      );
    }

    if (translatedText && status !== 'idle') {
      return (
        <Box
          sx={{
            p: 1.5,
            fontSize: '13px',
            lineHeight: 1.6,
            color: '#2b3442',
            opacity: status === 'stale' ? 0.5 : 1,
            whiteSpace: 'pre-wrap',
            overflow: 'auto',
            maxHeight: 280
          }}
        >
          {translatedText.split(/\n\n+/).map((para) => (
            <Typography key={para.slice(0, 24)} sx={{ mb: 1 }} variant="body2">
              {para}
            </Typography>
          ))}
        </Box>
      );
    }

    return (
      <Box
        sx={{
          p: 1.5,
          fontStyle: 'italic',
          color: '#7f97a8',
          minHeight: 110,
          display: 'flex',
          alignItems: 'center'
        }}
      >
        {t('itilGenius.messages.idleHint')}
      </Box>
    );
  };

  return (
    <Box sx={itilFieldBoxSx}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
        <Typography sx={{ fontWeight: 700, color: itilColors.navy, fontSize: 13 }}>
          {required && (
            <Box component="span" sx={{ color: itilColors.req, mr: 0.5 }}>
              *
            </Box>
          )}
          {label}
        </Typography>
        {inheritedBadge && (
          <Box
            sx={{
              fontSize: '10px',
              fontWeight: 700,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              color: '#3f7fa0',
              bgcolor: '#e7f2f9',
              border: '1px solid #c5dded',
              borderRadius: '20px',
              px: 1,
              py: 0.25
            }}
          >
            {inheritedBadge}
          </Box>
        )}
        {!inheritedBadge && (
          <Box
            sx={{
              fontSize: '10px',
              fontWeight: 700,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              color: '#3f7fa0',
              bgcolor: '#e7f2f9',
              border: '1px solid #c5dded',
              borderRadius: '20px',
              px: 1,
              py: 0.25
            }}
          >
            {t('itilGenius.badge.translatesToItil')}
          </Box>
        )}
      </Box>

      {showPermLock && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            bgcolor: '#eef1f5',
            border: '1px solid #d8dfe9',
            borderRadius: '7px',
            p: '9px 12px',
            mb: 1.5,
            fontSize: '12.5px',
            color: itilColors.inkSoft
          }}
        >
          <LockOutlinedIcon sx={{ fontSize: 16, color: '#74829a' }} />
          {t('itilGenius.messages.permLock')}
        </Box>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 1.5
        }}
      >
        <Box sx={itilColumnSx.orig}>
          <Box
            sx={{
              fontSize: '10.5px',
              fontWeight: 700,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              color: '#8a94a4',
              px: 1.5,
              py: 1,
              borderBottom: `1px solid ${itilColors.origBorder}`
            }}
          >
            {t('itilGenius.columns.original')}
          </Box>
          <TextField
            disabled={readOnly}
            inputProps={{ maxLength }}
            multiline
            onChange={(e) => onOriginalChange(e.target.value)}
            placeholder={t('common.commonProperties.comments')}
            sx={{
              '& .MuiOutlinedInput-root': {
                border: 'none',
                bgcolor: 'transparent',
                '& fieldset': { border: 'none' }
              }
            }}
            value={originalValue}
            variant="outlined"
            fullWidth
            minRows={5}
          />
        </Box>

        <Box sx={itilColumnSx.itil}>
          <Box
            sx={{
              fontSize: '10.5px',
              fontWeight: 700,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              color: '#3f7fa0',
              px: 1.5,
              py: 1,
              borderBottom: `1px solid ${itilColors.itilBorder}`,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            {t('itilGenius.columns.itil')}
            <ItilStatusBadge status={status} />
          </Box>
          {renderItilBody()}
        </Box>
      </Box>

      {!readOnly && (
        <ItilTranslateActions
          disabled={readOnly}
          hint={hint()}
          onContinueOriginal={onContinueOriginal}
          onTranslate={onTranslate}
          status={status}
        />
      )}
    </Box>
  );
}

export default ItilDualColumnField;
